import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { ClockIcon, TargetIcon, TrendingUpIcon2, PeopleIcon } from "../../../../images/icons/icons";
import ReactVortoxSpinner from "../../../common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../../payment-link-system/PaymentLinkTable";
import CmnFilters from "../../common/CmnFilters";
import { useDispatch, useSelector } from "react-redux";
import { fetchAgentManagementData } from "../../../../redux/prospect-reducers/prospectSlice";
import Modal from "react-modal";
import ModalHeadAndClose from "../../../payouts-wtf/common/ModalHeadAndClose";

const AgentManagement = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const { agentManagement, agentManagementLoading } = useSelector((state) => state?.prospects);

  // Tab state
  const [activeTab, setActiveTab] = useState("agent-overview");
  
  // Modal state
  const [modalValues, setModalValues] = useState({
    isModOpen: false,
    modName: "",
    selectedAgent: null,
  });

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isClusterManager = userInfoData?.account_type === "cluster_manager";


  const iniAgentOverviewFilters = {
    selectedGym:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedAgent: null,
    selectedLeadStage: null,
  };

  // Stuck Leads filters (full filters)
  const iniStuckLeadsFilters = {
    selectedGym:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedAgent: null,
    selectedLeadStage: null,
    leadAddDate: [{ from: "", to: "" }],
  };

  // Modal filters (separate from stuck leads)
  const iniModalFilters = {
    selectedGym:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedAgent: null,
    selectedLeadStage: null,
    leadAddDate: [{ from: "", to: "" }],
  };

  const [agentOverviewFilters, setAgentOverviewFilters] = useState(iniAgentOverviewFilters);
  const [stuckLeadsFilters, setStuckLeadsFilters] = useState(iniStuckLeadsFilters);
  const [modalFilters, setModalFilters] = useState(iniModalFilters);

  // Pagination state for each section
  const [agentOverviewPage, setAgentOverviewPage] = useState(0);
  const [agentOverviewPageLimit, setAgentOverviewPageLimit] = useState(10);
  
  const [stuckLeadsPage, setStuckLeadsPage] = useState(0);
  const [stuckLeadsPageLimit, setStuckLeadsPageLimit] = useState(10);
  
  const [modalPage, setModalPage] = useState(0);
  const [modalPageLimit, setModalPageLimit] = useState(10);

  // API filters state - initialize with empty values
  const [filters, setFilters] = useState({
    gym_id: "",
    agent_id: "",
    lead_stage: "",
    date_from: "",
    date_to: "",
  });

  // Filter functions for Agent Overview
  const onResetAgentOverviewFilters = () => {
    setAgentOverviewFilters(iniAgentOverviewFilters);
    setAgentOverviewPage(0); // Reset pagination when filters reset
  };

  // Filter functions for Stuck Leads
  const onResetStuckLeadsFilters = () => {
    setStuckLeadsFilters(iniStuckLeadsFilters);
    setStuckLeadsPage(0); // Reset pagination when filters reset
  };

  // Filter functions for Modal
  const onResetModalFilters = () => {
    setModalFilters(iniModalFilters);
    setModalPage(0); // Reset pagination when filters reset
  };

  // Pagination handlers
  const handleAgentOverviewPageChange = (newPage) => {
    setAgentOverviewPage(newPage);
  };

  const handleAgentOverviewPageLimitChange = (newLimit) => {
    setAgentOverviewPageLimit(newLimit);
    setAgentOverviewPage(0); // Reset to first page when limit changes
  };

  const handleStuckLeadsPageChange = (newPage) => {
    setStuckLeadsPage(newPage);
  };

  const handleStuckLeadsPageLimitChange = (newLimit) => {
    setStuckLeadsPageLimit(newLimit);
    setStuckLeadsPage(0); // Reset to first page when limit changes
  };

  const handleModalPageChange = (newPage) => {
    setModalPage(newPage);
  };

  const handleModalPageLimitChange = (newLimit) => {
    setModalPageLimit(newLimit);
    setModalPage(0); // Reset to first page when limit changes
  };

  // Sync filters when allFilters change (like SubscriptionExtenstionStructure)
  useEffect(() => {
    if (activeTab === "agent-overview") {
      const newFilters = {
        gym_id: agentOverviewFilters.selectedGym?.value || "",
        agent_id: agentOverviewFilters.selectedAgent?.value || "",
        lead_stage: agentOverviewFilters.selectedLeadStage?.value || "",
        date_from: "",
        date_to: "",
        page: agentOverviewPage + 1,
        limit: agentOverviewPageLimit,
      };
      setFilters(newFilters);
    } else if (activeTab === "stuck-leads") {
      const newFilters = {
        gym_id: stuckLeadsFilters.selectedGym?.value || "",
        agent_id: stuckLeadsFilters.selectedAgent?.value || "",
        lead_stage: stuckLeadsFilters.selectedLeadStage?.value || "",
        date_from: stuckLeadsFilters.leadAddDate[0]?.from || "",
        date_to: stuckLeadsFilters.leadAddDate[0]?.to || "",
        page: stuckLeadsPage + 1,
        limit: stuckLeadsPageLimit,
      };
      setFilters(newFilters);
    }
  }, [agentOverviewFilters, stuckLeadsFilters, activeTab, agentOverviewPage, agentOverviewPageLimit, stuckLeadsPage, stuckLeadsPageLimit]);

  // Sync modal filters when they change
  useEffect(() => {
    if (modalValues.isModOpen) {
      const newFilters = {
        gym_id: modalFilters.selectedGym?.value || "",
        agent_id: modalFilters.selectedAgent?.value || "",
        lead_stage: modalFilters.selectedLeadStage?.value || "",
        date_from: modalFilters.leadAddDate[0]?.from || "",
        date_to: modalFilters.leadAddDate[0]?.to || "",
        page: modalPage + 1,
        limit: modalPageLimit,
      };
      setFilters(newFilters);
    }
  }, [modalFilters, modalValues.isModOpen, modalPage, modalPageLimit]);

  // Reset pagination when filters change (like SubscriptionExtenstionStructure)
  useEffect(() => {
    if (activeTab === "agent-overview") {
      setAgentOverviewPage(0);
    } else if (activeTab === "stuck-leads") {
      setStuckLeadsPage(0);
    }
  }, [agentOverviewFilters, stuckLeadsFilters, activeTab]);

  // Reset modal pagination when modal filters change
  useEffect(() => {
    if (modalValues.isModOpen) {
      setModalPage(0);
    }
  }, [modalFilters, modalValues.isModOpen]);

  // Sync gym_id when userInfoData changes
  useEffect(() => {
    const gymData = !isAdmin && !isMarketing
      ? {
          label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
          value:
            userInfoData?.gym_id ||
            userInfoData?.user_id ||
            userInfoData?.uid ||
            "",
        }
      : "";

    setAgentOverviewFilters((prevs) => ({
      ...prevs,
      selectedGym: gymData,
    }));

    setStuckLeadsFilters((prevs) => ({
      ...prevs,
      selectedGym: gymData,
    }));

    setModalFilters((prevs) => ({
      ...prevs,
      selectedGym: gymData,
    }));
  }, [
    userInfoData?.gym_id,
    isAdmin,
    isMarketing,
    isGym,
    userInfoData?.name,
    userInfoData?.gym_name,
    userInfoData?.user_id,
    userInfoData?.uid,
  ]);

  useEffect(() => {
    if (!userInfoData?.token) return;
    dispatch(
      fetchAgentManagementData({
        filters,
        token: userInfoData?.token,
      })
    );
  }, [dispatch, userInfoData?.token, filters]);


  const toNumber = (val) => {
    const n = typeof val === "string" ? parseFloat(val) : Number(val);
    return Number.isFinite(n) ? n : 0;
  };

  // Cards data preparation
  const agentsArray = Array.isArray(agentManagement?.data)
    ? agentManagement.data
    : Array.isArray(agentManagement)
    ? agentManagement
    : [];

  const totalAgents = agentsArray.length;
  const totalStuckLeads = agentsArray.reduce((sum, a) => sum + toNumber(a?.stuck_leads), 0);
  const avgConversion = totalAgents
    ? (agentsArray.reduce((sum, a) => sum + toNumber(a?.conversion_rate), 0) / totalAgents)
    : 0;
  const avgPerformance = totalAgents
    ? (agentsArray.reduce((sum, a) => sum + toNumber(a?.performance_score), 0) / totalAgents)
    : 0;

  const statsData = [
    {
      title: "Total Agents",
      value: String(totalAgents),
      subtitle: "All active agents",
      icon: <PeopleIcon />,
      background:"blue"
    },
    {
      title: "Avg Conversion",
      value: `${avgConversion?.toFixed?.(1)}%`,
      subtitle: "Team average",
      icon: <TrendingUpIcon2 />,
       background:"sky-blue"
    },
    {
      title: "Total Stuck Leads",
      value: String(totalStuckLeads),
      subtitle: "Needs attention",
      icon: <ClockIcon />,
       background:"red"
    },
    {
      title: "Avg Performance",
      value: `${avgPerformance?.toFixed?.(1)}%`,
      subtitle: "Team performance",
      icon: <TargetIcon />,
      background:"orange"
    },
  ];


  const stuckLeadsData = agentsArray.flatMap((a) => {
    const alerts = Array.isArray(a?.stuck_leads_alert) ? a.stuck_leads_alert : [];
    return alerts.map((al) => ({
      leadID: al?.lead_id || "",
      agent: a?.agent_name || "Unknown",
      agentId: a?.agent_id || "",
      stage: al?.stage || "",
      daysStuck: toNumber(al?.days_stuck),
      daysStuckText: `${toNumber(al?.days_stuck)} days`,
      status: "stuck",
    }));
  });

  // Modal functions
  const handleOpenStuckLeadsModal = (agent) => {
    setModalValues({
      isModOpen: true,
      modName: "stuck-leads-modal",
      selectedAgent: agent,
    });
  };

  const handleCloseModal = () => {
    setModalValues({
      isModOpen: false,
      modName: "",
      selectedAgent: null,
    });
  };

  // Agent Overview Table Columns
  const agentTableColumns = [
    {
      dataField: "agent_name",
      text: "Agent Name",
      formatter: (cell, row) => (
        <div>
          <div className="fw-medium">{cell || "Unknown"}</div>
          <small className="text-muted">{row?.email || "No email"}</small>
        </div>
      ),
    },
    {
      dataField: "total_leads",
      text: "Total Leads",
      formatter: (cell, row) => (
        <button
          className="btn btn-link p-0 text-primary fw-medium"
          onClick={() => handleOpenStuckLeadsModal(row)}
          style={{ textDecoration: "underline" }}
        >
          {cell || "0"}
        </button>
      ),
    },
    {
      dataField: "active_leads",
      text: "Active Leads",
      formatter: (cell) => <span className="minWidth100">{cell || "0"}</span>,
    },
    {
      dataField: "stuck_leads",
      text: "Stuck Leads",
      formatter: (cell) => <span className="minWidth100">{cell || "0"}</span>,
    },
    {
      dataField: "conversion_rate",
      text: "Conversion Rate",
      formatter: (cell) => <span className="minWidth100">{toNumber(cell).toFixed(1)}%</span>,
    },
    {
      dataField: "performance_score",
      text: "Performance Score",
      formatter: (cell) => <span className="minWidth100">{toNumber(cell).toFixed(1)}%</span>,
    },
  ];

  // Stuck Leads Table Columns
  const stuckLeadsTableColumns = [
    // {
    //   dataField: "leadID",
    //   text: "Lead ID",
    //   formatter: (cell) => <span className="fw-medium">{cell || "N/A"}</span>,
    // },
    {
      dataField: "agent",
      text: "Agent",
      formatter: (cell) => <span className="minWidth150">{cell || "Unknown"}</span>,
    },
    {
      dataField: "stage",
      text: "Stage",
      formatter: (cell) => {
        const stage = cell || "Unknown";
        const getStagePillClass = (stageText) => {
          const stage = stageText.toLowerCase();
          if (stage.includes("fresh")) {
            return "stage-pill stage-pill-fresh";
          } else if (stage.includes("wtf_one_day_plan")) {
            return "stage-pill stage-pill-renewal";
          } else if (stage.includes("pending")) {
            return "stage-pill stage-pill-unpaid";
          } else if (stage.includes("closure_expected")) {
            return "stage-pill stage-pill-subscribed";
          } else {
            return "stage-pill stage-pill-default";
          }
        };
        return <span className={getStagePillClass(stage)}>{stage.replace(/_/g, " ")}</span>;
      },
    },
    {
      dataField: "daysStuck",
      text: "Days Stuck",
      formatter: (cell) => (
        <span className={`minWidth100 ${cell > 30 ? "text-danger fw-bold" : cell > 7 ? "text-warning fw-medium" : ""}`}>
          {cell} days
        </span>
      ),
    },
  ];

  // Filter stuck leads by selected agent for modal
  const getFilteredStuckLeads = () => {
    let filtered = stuckLeadsData;
    
    if (modalValues.selectedAgent) {
      filtered = filtered.filter(lead => lead.agentId === modalValues.selectedAgent.agent_id);
    }
    
    // Apply modal filters
    if (modalFilters.selectedAgent?.value) {
      filtered = filtered.filter(lead => lead.agentId === modalFilters.selectedAgent.value);
    }
    
    if (modalFilters.selectedLeadStage?.value) {
      filtered = filtered.filter(lead => lead.stage === modalFilters.selectedLeadStage.value);
    }
    
    
    return filtered;
  };

  // Filter stuck leads for Stuck Leads tab
  const getFilteredStuckLeadsForTab = () => {
    let filtered = stuckLeadsData;
    
    if (stuckLeadsFilters.selectedAgent?.value) {
      filtered = filtered.filter(lead => lead.agentId === stuckLeadsFilters.selectedAgent.value);
    }
    
    if (stuckLeadsFilters.selectedLeadStage?.value) {
      filtered = filtered.filter(lead => lead.stage === stuckLeadsFilters.selectedLeadStage.value);
    }
    
    
    return filtered;
  };

  return (
    <div>
      {/* Stats Cards */}
      <div className="lms-closure-agent-management-stats-cards-container">
        {agentManagementLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 140 }}>
            <ReactVortoxSpinner />
          </div>
        ) : (
          statsData?.map((stat, index) => (
            <div 
              key={index} 
              className="lms-closure-agent-management-stat-card"
              data-background={stat.background}
            >
              <div className="lms-closure-agent-management-stat-header">
                <span className="lms-closure-agent-management-stat-title">{stat.title}</span>
                <div className="lms-closure-agent-management-stat-icon">{stat.icon}</div>
              </div>
              <div className="lms-closure-agent-management-stat-value">{stat.value}</div>
              <div className="lms-closure-agent-management-stat-subtitle">{stat.subtitle}</div>
            </div>
          ))
        )}
      </div>

      {/* Tabs */}
      <div className="lms-closure-agent-management-tabs">
        <div className="lms-closure-agent-management-tab-buttons">
          <button
            className={`lms-closure-agent-management-tab-btn ${activeTab === "agent-overview" ? "active" : ""}`}
            onClick={() => setActiveTab("agent-overview")}
          >
            Agent Overview
          </button>
          <button
            className={`lms-closure-agent-management-tab-btn ${activeTab === "stuck-leads" ? "active" : ""}`}
            onClick={() => setActiveTab("stuck-leads")}
          >
            Stuck Leads
          </button>
                </div>
      </div>

      {/* Tab Content */}
      {activeTab === "agent-overview" && (
        <div className="lms-closure-agent-management-stuck-leads-container">
          <Row>
            <Col>
              <CmnFilters
                from="agent-overview"
                userInfoData={userInfoData}
                onClickReset={onResetAgentOverviewFilters}
                filters={agentOverviewFilters}
                setFilters={setAgentOverviewFilters}
                searchFilterApplied={
                  (agentOverviewFilters.selectedGym?.value !== "" && agentOverviewFilters.selectedGym?.value !== iniAgentOverviewFilters.selectedGym?.value) ||
                  (agentOverviewFilters.selectedAgent?.value !== "" && agentOverviewFilters.selectedAgent?.value !== null) ||
                  (agentOverviewFilters.selectedLeadStage?.value !== "" && agentOverviewFilters.selectedLeadStage?.value !== null)
                }
              />
            </Col>
          </Row>
          
          {agentManagementLoading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160 }}>
              <ReactVortoxSpinner />
                  </div>
          ) : (
            <div className="fullWidthFlexCenter overflowAuto separatedTable">
              <PaymentLinkTable
                tableData={agentsArray || []}
                tableColumns={agentTableColumns}
                tablePagination={[]}
                isPage={agentOverviewPage}
                setIsPage={handleAgentOverviewPageChange}
                pageLimit={agentOverviewPageLimit}
                setPageLimit={handleAgentOverviewPageLimitChange}
              />
            </div>
        )}
      </div>
      )}

      {activeTab === "stuck-leads" && (
        <div className="lms-closure-agent-management-stuck-leads-container">
          <Row>
            <Col>
              <CmnFilters
                from="agent-management"
                userInfoData={userInfoData}
                onClickReset={onResetStuckLeadsFilters}
                filters={stuckLeadsFilters}
                setFilters={setStuckLeadsFilters}
                searchFilterApplied={
                  (stuckLeadsFilters.selectedGym?.value !== "" && stuckLeadsFilters.selectedGym?.value !== iniStuckLeadsFilters.selectedGym?.value) ||
                  (stuckLeadsFilters.selectedAgent?.value !== "" && stuckLeadsFilters.selectedAgent?.value !== null) ||
                  (stuckLeadsFilters.selectedLeadStage?.value !== "" && stuckLeadsFilters.selectedLeadStage?.value !== null) ||
                  (stuckLeadsFilters.leadAddDate[0]?.from !== "" || stuckLeadsFilters.leadAddDate[0]?.to !== "")
                }
              />
            </Col>
          </Row>

        {agentManagementLoading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160 }}>
            <ReactVortoxSpinner />
          </div>
        ) : (
            <div className="fullWidthFlexCenter overflowAuto separatedTable">
              <PaymentLinkTable
                tableData={getFilteredStuckLeadsForTab() || []}
                tableColumns={stuckLeadsTableColumns}
                tablePagination={[]}
                isPage={stuckLeadsPage}
                setIsPage={handleStuckLeadsPageChange}
                pageLimit={stuckLeadsPageLimit}
                setPageLimit={handleStuckLeadsPageLimitChange}
              />
            </div>
        )}
      </div>
      )}

      {/* Modal for Agent Stuck Leads */}
      <Modal
        isOpen={modalValues.isModOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Agent Stuck Leads Modal"
        ariaHideApp={false}
      >
        <div className="createNewCTOModalContainer">
          <ModalHeadAndClose
            title={`Stuck Leads - ${modalValues.selectedAgent?.agent_name || "Unknown Agent"}`}
            onClose={handleCloseModal}
          />
          
          <div className="p-3">
            <Row>
              <Col>
                <CmnFilters
                  from="agent-management"
                  userInfoData={userInfoData}
                  onClickReset={onResetModalFilters}
                  filters={modalFilters}
                  setFilters={setModalFilters}
                  searchFilterApplied={
                    (modalFilters.selectedGym?.value !== "" && modalFilters.selectedGym?.value !== iniModalFilters.selectedGym?.value) ||
                    (modalFilters.selectedAgent?.value !== "" && modalFilters.selectedAgent?.value !== null) ||
                    (modalFilters.selectedLeadStage?.value !== "" && modalFilters.selectedLeadStage?.value !== null) ||
                    (modalFilters.leadAddDate[0]?.from !== "" || modalFilters.leadAddDate[0]?.to !== "")
                  }
                />
              </Col>
            </Row>
            
            <div className="fullWidthFlexCenter overflowAuto separatedTable">
              <PaymentLinkTable
                tableData={getFilteredStuckLeads() || []}
                tableColumns={stuckLeadsTableColumns}
                tablePagination={[]}
                isPage={modalPage}
                setIsPage={handleModalPageChange}
                pageLimit={modalPageLimit}
                setPageLimit={handleModalPageLimitChange}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentManagement;
